// Aba "Editar dados" do contrato. Formulário simples de edição de campos
// do contrato (título, datas, valor, ART legacy, observações).
// Wave 2 (Antd) — 2026-06-01.

import { useState } from 'react';
import axios from 'axios';
import {
  Form, Input, InputNumber, Select, DatePicker, Button, Row, Col,
  App as AntApp, Card,
} from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { API_URL } from '../../config';
import RangePrazo, { somarDias } from '../../components/RangePrazo';
import { getToken } from './utils';
import { STATUS_CONTRATO } from './types';
import type { Contrato } from './types';

const { TextArea } = Input;

export function EditarContrato({ contrato, onSaved }: { contrato: Contrato; onSaved: () => void }) {
  return <AntApp><EditarContratoInner contrato={contrato} onSaved={onSaved} /></AntApp>;
}

function EditarContratoInner({ contrato, onSaved }: { contrato: Contrato; onSaved: () => void }) {
  const { message } = AntApp.useApp();
  const [form, setForm] = useState({
    titulo: contrato.titulo,
    responsavel_email: contrato.responsavel_email || '',
    gestor_email: contrato.gestor_email || '',
    status: contrato.status,
    data_inicio_prevista: (contrato.data_inicio_prevista || '').slice(0, 10),
    data_termino_previsto: (contrato.data_termino_previsto || '').slice(0, 10),
    data_inicio_real: (contrato.data_inicio_real || '').slice(0, 10),
    data_termino_real: (contrato.data_termino_real || '').slice(0, 10),
    valor_contratado: contrato.valor_contratado?.toString() || '',
    forma_pagamento: contrato.forma_pagamento || '',
    art_numero: contrato.art_numero || '',
    art_data_emissao: (contrato.art_data_emissao || '').slice(0, 10),
    observacoes: contrato.observacoes || '',
  });
  const [salvando, setSalvando] = useState(false);

  async function salvar() {
    setSalvando(true);
    try {
      const token = await getToken();
      const updates: any = { action: 'op_atualizar_contrato', id: contrato.id };
      for (const k of Object.keys(form) as Array<keyof typeof form>) {
        const v = form[k];
        if (k.startsWith('data_') && !v) updates[k] = null;
        else if (k === 'valor_contratado') updates[k] = v ? parseFloat(v) : null;
        else updates[k] = v || null;
      }
      await axios.post(`${API_URL}/comercial/propostas-geradas`, updates,
        { headers: { Authorization: token } });
      onSaved();
      message.success('Alterações salvas.');
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    } finally { setSalvando(false); }
  }

  return (
    <Card size="small" style={{ maxWidth: 900 }}>
      <Form layout="vertical" disabled={salvando}>
        <Row gutter={12}>
          <Col span={24}>
            <Form.Item label="Título do contrato">
              <Input value={form.titulo} onChange={e => setForm({ ...form, titulo: e.target.value })} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="Responsável (e-mail)">
              <Input value={form.responsavel_email}
                onChange={e => setForm({ ...form, responsavel_email: e.target.value })} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="Gestor (e-mail)">
              <Input value={form.gestor_email}
                onChange={e => setForm({ ...form, gestor_email: e.target.value })} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="Status">
              <Select value={form.status} onChange={v => setForm({ ...form, status: v })}
                options={Object.entries(STATUS_CONTRATO).map(([k, v]) => ({ value: k, label: v.label }))} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="Valor contratado (R$)">
              <InputNumber value={form.valor_contratado ? parseFloat(form.valor_contratado) : null}
                onChange={(v) => setForm({ ...form, valor_contratado: v != null ? String(v) : '' })}
                min={0} step={1000} style={{ width: '100%' }}
                formatter={(val) => `R$ ${(val || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}
                parser={(val) => Number(String(val).replace(/[^\d.-]/g, '')) as any} />
            </Form.Item>
          </Col>
          <Col span={24}>
            {/* Range previsto: início + prazo (dias) → término calculado */}
            <RangePrazo
              value={{
                inicio: form.data_inicio_prevista || null,
                prazo_dias: (form.data_inicio_prevista && form.data_termino_previsto)
                  ? Math.round((new Date(form.data_termino_previsto + 'T12:00:00').getTime()
                      - new Date(form.data_inicio_prevista + 'T12:00:00').getTime()) / 86400000)
                  : null,
                conclusao: form.data_termino_previsto || null,
              }}
              onChange={v => setForm({
                ...form,
                data_inicio_prevista: v.inicio || '',
                data_termino_previsto: v.conclusao || somarDias(v.inicio, v.prazo_dias) || '',
              })}
              labelInicio="Início previsto"
              labelConclusao="Término previsto"
            />
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="Início real">
              <DatePicker value={form.data_inicio_real ? dayjs(form.data_inicio_real) : null}
                onChange={(d) => setForm({ ...form, data_inicio_real: d ? d.format('YYYY-MM-DD') : '' })}
                format="DD/MM/YYYY" style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="Término real">
              <DatePicker value={form.data_termino_real ? dayjs(form.data_termino_real) : null}
                onChange={(d) => setForm({ ...form, data_termino_real: d ? d.format('YYYY-MM-DD') : '' })}
                format="DD/MM/YYYY" style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="ART (número CREA)">
              <Input value={form.art_numero} placeholder="Ex.: RS20251234567"
                onChange={e => setForm({ ...form, art_numero: e.target.value })} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="ART — emissão">
              <DatePicker value={form.art_data_emissao ? dayjs(form.art_data_emissao) : null}
                onChange={(d) => setForm({ ...form, art_data_emissao: d ? d.format('YYYY-MM-DD') : '' })}
                format="DD/MM/YYYY" style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item label="Forma de pagamento">
              <Input value={form.forma_pagamento}
                onChange={e => setForm({ ...form, forma_pagamento: e.target.value })}
                placeholder="Ex.: 30/60/90 dias após assinatura" />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item label="Observações">
              <TextArea value={form.observacoes}
                onChange={e => setForm({ ...form, observacoes: e.target.value })}
                autoSize={{ minRows: 3, maxRows: 8 }} />
            </Form.Item>
          </Col>
          <Col span={24} style={{ textAlign: 'right' }}>
            <Button type="primary" size="large" icon={<SaveOutlined />}
              onClick={salvar} loading={salvando}>
              Salvar alterações
            </Button>
          </Col>
        </Row>
      </Form>
    </Card>
  );
}
